import React from 'react';
import { Box, useTheme } from '@mui/material';
import { checkDraw, TIMING, EASING } from './animations';

interface AnimatedCheckIconProps {
  /** Whether to play the draw animation */
  animate?: boolean;
  /** Size in pixels */
  size?: number;
  /** Override color (defaults to success.main) */
  color?: string;
}

/**
 * Animated checkmark icon with SVG stroke draw animation.
 *
 * When `animate` is true, the checkmark draws itself in.
 * Used for step completion feedback in the activation checklist.
 *
 * Design: Filled circle background with stroke checkmark for
 * professional B2B appearance (Stripe/Linear style).
 */
export function AnimatedCheckIcon({
  animate = false,
  size = 20,
  color,
}: AnimatedCheckIconProps) {
  const theme = useTheme();
  const checkColor = color || theme.palette.success.main;

  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      {/* Background circle with subtle fill */}
      <circle
        cx="12"
        cy="12"
        r="10"
        fill={checkColor}
        opacity={0.15}
      />

      {/* Outer circle stroke */}
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="none"
        stroke={checkColor}
        strokeWidth={1.5}
        opacity={0.3}
      />

      {/* Checkmark path with draw animation */}
      <path
        d="M8 12l2.5 2.5L16 9"
        fill="none"
        stroke={checkColor}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          strokeDasharray: 24,
          strokeDashoffset: animate ? 0 : 24,
          animation: animate
            ? `${checkDraw} ${TIMING.smooth} ${EASING.decelerate} forwards`
            : 'none',
        }}
      />
    </Box>
  );
}

/**
 * Static completed check icon (no animation).
 * Used for already-completed steps.
 */
export function CompletedCheckIcon({
  size = 20,
  color,
}: Omit<AnimatedCheckIconProps, 'animate'>) {
  const theme = useTheme();
  const checkColor = color || theme.palette.success.main;

  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
      }}
    >
      {/* Solid filled circle */}
      <circle cx="12" cy="12" r="10" fill={checkColor} />

      {/* White checkmark */}
      <path
        d="M8 12l2.5 2.5L16 9"
        fill="none"
        stroke="white"
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  );
}
